import React, { useState, useEffect } from "react";
import {
  Users, Search, Edit, Trash2, X, Info, Briefcase, DollarSign, 
  MapPin, Laptop, Plus, FolderKanban, Eye, User, Phone, Mail, Globe, Save, CheckCircle, Loader2, Clock, BarChart3
} from "lucide-react";
import ActivityLogs from "../components/ActivityLogs";
import Reports from "../components/Reports";

// --- API Imports ---
import { 
  createEmployee, 
  getAdminEmployeeView,
  getAllEmployeesList,
  adminUpdateBasicInfo
} from "../services/employee.service";

// --- Custom Components ---
function Input({ label, value, onChange, type = "text", placeholder = "", className = "" }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label className="text-sm text-gray-600 font-semibold font-poppins">{label}</label>
      <input
        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition duration-150 font-roboto bg-white text-sm"
        value={value || ''}
        onChange={(e) => onChange && onChange(e.target.value)}
        type={type}
        placeholder={placeholder}
      />
    </div>
  );
}

function DataField({ label, value, icon: Icon }) {
  return (
    <div className="flex flex-col p-3 border-b border-gray-50 last:border-0">
      <div className="flex items-center gap-2 text-[10px] font-bold text-teal-600 uppercase font-poppins">
        {Icon && <Icon size={12} />} {label}
      </div>
      <div className="text-sm text-gray-800 font-medium mt-1 font-roboto">
        {value || "—"}
      </div>
    </div>
  );
}

function ArrayEditor({ title, items = [], itemStructure, renderItem, onAddItem, onRemoveItem, icon: Icon }) {
  return (
    <div className="border border-gray-200 p-4 rounded-xl space-y-3 bg-gray-50/50">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={18} className="text-teal-600" />}
          <h4 className="text-md font-bold text-gray-700 font-poppins">{title} ({items?.length || 0})</h4>
        </div>
      </div>
      {items?.map((item, index) => (
        <div key={index} className="p-4 border bg-white rounded-lg shadow-sm relative group">
          <button
            onClick={() => onRemoveItem(index)}
            className="absolute top-2 right-2 text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-50 transition"
          >
            <X size={16} />
          </button>
          {renderItem(item, index)}
        </div>
      ))}
      <button
        onClick={() => onAddItem(itemStructure)}
        className="text-sm text-teal-600 flex items-center gap-1 hover:bg-teal-50 font-bold border border-dashed border-teal-300 p-3 rounded-lg mt-2 w-full justify-center transition"
      >
        <Plus size={16} /> Add New {title}
      </button>
    </div>
  );
}

const defaultEmployee = {
  id: null, employeeCode: "", fullName: "", firstName: "", middleName: "", surname: "", employmentType: "Employed (Full-time)", employmentStatus: "Active", doj: "", dob: "", gender: "Male", bloodGroup: "", email: "", personalEmail: "", status: "Active", bankName: "", accountNo: "", ifsc: "", assetsList: [], projectsList: [],
};

const LOCATIONS = ["Mumbai", "Bangalore", "Chennai", "Hyderabad", "Pune", "Delhi"];
const EMPLOYMENT_TYPES = [
  "Employed (Full-time)",
  "Employed (Part-time)", 
  "Self-employed / Freelancer",
  "Contract / Temporary",
  "Intern / Trainee",
  "Unemployed (actively looking for work)",
  "Not in labor force (e.g., student, retired, homemaker)",
  "On leave (maternity, medical, sabbatical)"
];
const EMPLOYMENT_STATUS = [
  "Active",
  "Inactive", 
  "Terminated",
  "On Leave",
  "Suspended",
  "Resigned",
  "Retired"
];
const TAB_CONFIG = [
  { id: 'basic', label: 'Basic Info', icon: Info },
  { id: 'org', label: 'Organization', icon: Briefcase },
  { id: 'statutory', label: 'Bank & Statutory', icon: DollarSign },
  { id: 'assets', label: 'Assets', icon: Laptop },
  { id: 'projects', label: 'Projects', icon: FolderKanban },
  { id: 'activity', label: 'Activity Logs', icon: Clock },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
];

export default function EmployeeMaster() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [filterLoc, setFilterLoc] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentEmp, setCurrentEmp] = useState(defaultEmployee);
  const [activeTab, setActiveTab] = useState('basic');
  const [isSaving, setIsSaving] = useState(false);

  // --- Fetch Data Logic ---
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getAllEmployeesList(); 
      console.log("Full Employee List Data:", response);

      const rawList = Array.isArray(response) ? response : (response?.data || []);

      const mappedList = rawList.map(emp => ({
        ...defaultEmployee,
        id: emp.emp_code || emp.id || Math.random(),
        employeeCode: emp.emp_code || "N/A",
        fullName: emp.full_name || emp.name || "N/A",
        doj: emp.doj || emp.date_of_joining || "N/A",
        dept: emp.department_name || emp.department || "N/A",
        location: emp.location || "N/A",
        status: emp.status || "Active"
      }));

      setEmployees(mappedList);
      setFiltered(mappedList);
      
      // Fetch stats
      const statsResponse = await fetch('http://localhost:8000/admin/employees/stats');
      const statsData = await statsResponse.json();
      setStats(statsData);
    } catch (error) {
      console.error("Fetch Error Details:", error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- Search & Filter ---
  useEffect(() => {
    let list = employees.filter(e => 
      (e.fullName?.toLowerCase().includes(search.toLowerCase()) || e.employeeCode?.toLowerCase().includes(search.toLowerCase())) &&
      (filterLoc === "All" ? true : e.location === filterLoc) &&
      (filterStatus === "All" ? true : e.status === filterStatus)
    );
    setFiltered(list);
  }, [search, employees, filterLoc, filterStatus]);

  const handleSave = async () => {
    if (!currentEmp.employeeCode || !currentEmp.firstName) {
        alert("Employee Code and First Name are required.");
        return;
    }
    try {
      setIsSaving(true);
      if (currentEmp.id && typeof currentEmp.id !== 'number') {
        const updatePayload = {};
        if (currentEmp.firstName) updatePayload.first_name = currentEmp.firstName;
        if (currentEmp.middleName) updatePayload.middle_name = currentEmp.middleName;
        if (currentEmp.surname) updatePayload.last_name = currentEmp.surname;
        if (currentEmp.gender) updatePayload.gender = currentEmp.gender;
        if (currentEmp.doj) updatePayload.date_of_joining = currentEmp.doj;
        if (currentEmp.email) updatePayload.official_email = currentEmp.email;
        if (currentEmp.dept) updatePayload.department = currentEmp.dept;
        if (currentEmp.designation) updatePayload.designation = currentEmp.designation;
        if (currentEmp.location) updatePayload.location = currentEmp.location;
        if (currentEmp.status) updatePayload.status = currentEmp.status;
        
        console.log("Update Payload:", updatePayload);
        await adminUpdateBasicInfo(currentEmp.employeeCode, updatePayload);
      } else {
        await createEmployee(currentEmp);
      }
      await fetchData();
      setShowModal(false);
    } catch (error) {
      console.error("Save error:", error);
      alert("Error saving employee data: " + error);
    } finally {
      setIsSaving(false);
    }
  };

  const openEditModal = (emp = defaultEmployee) => {
    setCurrentEmp({ ...emp });
    setActiveTab('basic');
    setShowModal(true);
  };

  const openViewModal = async (emp) => {
    try {
      const detailedEmp = await getAdminEmployeeView(emp.employeeCode);
      setCurrentEmp(detailedEmp || emp);
      setShowViewModal(true);
    } catch (error) {
      setCurrentEmp(emp);
      setShowViewModal(true);
    }
  };

  const updateField = (field, value) => {
    setCurrentEmp(prev => {
      const updated = { ...prev, [field]: value };
      if (['firstName', 'middleName', 'surname'].includes(field)) {
        updated.fullName = [updated.firstName, updated.middleName, updated.surname].filter(Boolean).join(" ");
      }
      return updated;
    });
  };

  const updateNestedArr = (key, index, field, value) => {
    const newList = [...(currentEmp[key] || [])];
    newList[index] = { ...newList[index], [field]: value };
    setCurrentEmp({...currentEmp, [key]: newList});
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <div className="space-y-6">
            <h4 className="text-lg font-bold text-teal-600 border-b pb-2">Identity & Contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input label="Employee Code" value={currentEmp.employeeCode} onChange={(v) => updateField('employeeCode', v)} />
              <Input label="First Name" value={currentEmp.firstName} onChange={(v) => updateField('firstName', v)} />
              <Input label="Middle Name" value={currentEmp.middleName} onChange={(v) => updateField('middleName', v)} />
              <Input label="Surname" value={currentEmp.surname} onChange={(v) => updateField('surname', v)} />
              <div>
                <label className="text-sm text-gray-600 font-semibold">Gender</label>
                <select className="w-full border p-2 rounded-lg mt-1 bg-white text-sm" value={currentEmp.gender} onChange={(e) => updateField('gender', e.target.value)}>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <Input label="Date of Joining" value={currentEmp.doj} type="date" onChange={(v) => updateField('doj', v)} />
              <Input label="Work Email" value={currentEmp.email} onChange={(v) => updateField('email', v)} />
            </div>
          </div>
        );
      case 'org':
        return (
          <div className="space-y-6">
            <h4 className="text-lg font-bold text-teal-600 border-b pb-2">Organization</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Department" value={currentEmp.dept} onChange={(v) => updateField('dept', v)} />
              <Input label="Designation" value={currentEmp.designation} onChange={(v) => updateField('designation', v)} />
              <div>
                <label className="text-sm text-gray-600 font-semibold">Work Location</label>
                <select className="w-full border p-2 rounded-lg mt-1 bg-white text-sm" value={currentEmp.location} onChange={(e) => updateField('location', e.target.value)}>
                  {LOCATIONS.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-600 font-semibold">Employment Type</label>
                <select className="w-full border p-2 rounded-lg mt-1 bg-white text-sm" value={currentEmp.employmentType} onChange={(e) => updateField('employmentType', e.target.value)}>
                  {EMPLOYMENT_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-600 font-semibold">Employment Status</label>
                <select className="w-full border p-2 rounded-lg mt-1 bg-white text-sm" value={currentEmp.employmentStatus} onChange={(e) => updateField('employmentStatus', e.target.value)}>
                  {EMPLOYMENT_STATUS.map(status => <option key={status} value={status}>{status}</option>)}
                </select>
              </div>
            </div>
          </div>
        );
      case 'statutory':
        return (
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-teal-600 border-b pb-2">Bank Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Bank Name" value={currentEmp.bankName} onChange={(v) => updateField('bankName', v)} />
              <Input label="Account Number" value={currentEmp.accountNo} onChange={(v) => updateField('accountNo', v)} />
              <Input label="IFSC Code" value={currentEmp.ifsc} onChange={(v) => updateField('ifsc', v)} />
            </div>
          </div>
        );
      case 'assets':
        return (
          <ArrayEditor 
            title="Company Assets" icon={Laptop} items={currentEmp.assetsList || []}
            itemStructure={{ name: "", sn: "", status: "Assigned" }}
            onAddItem={(item) => setCurrentEmp({...currentEmp, assetsList: [...(currentEmp.assetsList || []), item]})}
            onRemoveItem={(idx) => setCurrentEmp({...currentEmp, assetsList: currentEmp.assetsList.filter((_, i) => i !== idx)})}
            renderItem={(asset, idx) => (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-6">
                <Input label="Asset Name" value={asset.name} onChange={(v) => updateNestedArr('assetsList', idx, 'name', v)} />
                <Input label="Serial Number" value={asset.sn} onChange={(v) => updateNestedArr('assetsList', idx, 'sn', v)} />
              </div>
            )}
          />
        );
      case 'projects':
        return (
          <ArrayEditor 
            title="Projects" icon={FolderKanban} items={currentEmp.projectsList || []}
            itemStructure={{ projectName: "", reportingManager: "" }}
            onAddItem={(item) => setCurrentEmp({...currentEmp, projectsList: [...(currentEmp.projectsList || []), item]})}
            onRemoveItem={(idx) => setCurrentEmp({...currentEmp, projectsList: currentEmp.projectsList.filter((_, i) => i !== idx)})}
            renderItem={(proj, idx) => (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-6">
                <Input label="Project Name" value={proj.projectName} onChange={(v) => updateNestedArr('projectsList', idx, 'projectName', v)} />
                <Input label="Manager" value={proj.reportingManager} onChange={(v) => updateNestedArr('projectsList', idx, 'reportingManager', v)} />
              </div>
            )}
          />
        );
      case 'activity':
        return (
          <div className="space-y-4">
            <ActivityLogs moduleName="Employee Master" />
          </div>
        );
      case 'reports':
        return (
          <div className="space-y-4">
            <Reports moduleName="Employee Master" />
          </div>
        );
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8 font-roboto text-gray-800">
      <div className="max-w-6xl mx-auto">
        <header className="mb-8 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2 font-poppins">
            <Users className="text-teal-600" /> Employee Master
          </h1>
          <button 
            onClick={() => openEditModal()}
            className="bg-teal-600 text-white px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 hover:bg-teal-700 shadow-lg"
          >
            <Plus size={20} /> Add New
          </button>
        </header>

        {/* Stats Cards */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-800">{stats.total}</div>
              <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Total</div>
            </div>
            <div className="text-center border-l pl-6">
              <div className="text-3xl font-bold text-green-600">{stats.by_status?.['Active'] || 0}</div>
              <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Active</div>
            </div>
            <div className="text-center border-l pl-6">
              <div className="text-3xl font-bold text-yellow-600">{stats.by_status?.['Inactive'] || 0}</div>
              <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Inactive</div>
            </div>
            <div className="text-center border-l pl-6">
              <div className="text-3xl font-bold text-orange-600">{stats.by_status?.['Resigned'] || 0}</div>
              <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Resigned</div>
            </div>
            <div className="text-center border-l pl-6">
              <div className="text-3xl font-bold text-purple-600">{stats.by_status?.['Exit Initiated'] || 0}</div>
              <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Exit Initiated</div>
            </div>
            <div className="text-center border-l pl-6">
              <div className="text-3xl font-bold text-red-600">{stats.by_status?.['Terminated'] || 0}</div>
              <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Terminated</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-6 flex flex-wrap gap-4">
          <div className="flex-1 min-w-[250px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input 
              className="w-full pl-10 pr-4 py-2 border rounded-lg bg-gray-50 outline-none focus:ring-2 focus:ring-teal-500" 
              placeholder="Search by name or code..." 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
            />
          </div>
          <select 
            className="border rounded-lg p-2 bg-gray-50 outline-none" 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Resigned">Resigned</option>
            <option value="Exit Initiated">Exit Initiated</option>
            <option value="Terminated">Terminated</option>
          </select>
          <select 
            className="border rounded-lg p-2 bg-gray-50 outline-none" 
            value={filterLoc} 
            onChange={(e) => setFilterLoc(e.target.value)}
          >
            <option value="All">All Locations</option>
            {LOCATIONS.map(loc => <option key={loc} value={loc}>{loc}</option>)}
          </select>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border">
            <Loader2 className="animate-spin text-teal-600 mb-4" size={40} />
            <p className="text-gray-500 font-medium">Loading employee records...</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b">
                  <tr className="font-poppins text-xs uppercase text-gray-500">
                    <th className="p-4">DOJ</th>
                    <th className="p-4">Emp Code</th>
                    <th className="p-4">Name</th>
                    <th className="p-4">Department</th>
                    <th className="p-4">Location</th>
                    <th className="p-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length > 0 ? filtered.map(emp => (
                    <tr key={emp.employeeCode} className="border-b hover:bg-teal-50/30 transition">
                      <td className="p-4 text-sm text-gray-600">{emp.doj || '—'}</td>
                      <td className="p-4">
                        <button onClick={() => openViewModal(emp)} className="text-teal-700 font-bold hover:underline">
                          {emp.employeeCode}
                        </button>
                      </td>
                      <td className="p-4">
                        <div className="font-bold text-gray-800">{emp.fullName}</div>
                        <div className={`text-[10px] inline-block px-2 rounded-full font-bold ${emp.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {emp.status}
                        </div>
                      </td>
                      <td className="p-4 text-sm text-gray-600">{emp.dept}</td>
                      <td className="p-4 text-sm text-gray-600"><MapPin size={14} className="inline mr-1" /> {emp.location}</td>
                      <td className="p-4 flex justify-center">
                        <button onClick={() => openEditModal(emp)} className="text-teal-600 hover:bg-teal-100 p-2 rounded-lg"><Edit size={18} /></button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="6" className="p-10 text-center text-gray-400">No employees found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* VIEW MODAL */}
      {showViewModal && (
        <div className="fixed inset-0 bg-gray-900/70 flex justify-center items-center z-[100] p-4 backdrop-blur-sm">
          <div className="bg-white w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col rounded-2xl">
            <div className="bg-gray-50 p-6 relative border-b">
              <button onClick={() => setShowViewModal(false)} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600"><X size={24} /></button>
              <div className="flex gap-6 items-center">
                <div className="h-16 w-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold uppercase">
                  {currentEmp.fullName?.charAt(0)}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{currentEmp.fullName}</h2>
                  <p className="text-gray-500">{currentEmp.employeeCode} | {currentEmp.designation || 'Staff'}</p>
                </div>
              </div>
            </div>
            <div className="overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <DataField label="Department" value={currentEmp.dept} icon={Briefcase} />
              <DataField label="Joining Date" value={currentEmp.doj} icon={Info} />
              <DataField label="Email" value={currentEmp.email} icon={Mail} />
              <DataField label="Location" value={currentEmp.location} icon={MapPin} />
              <DataField label="Status" value={currentEmp.status} icon={CheckCircle} />
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-[100] p-4 backdrop-blur-sm">
          <div className="bg-white w-full max-w-5xl h-[85vh] overflow-hidden flex flex-col rounded-2xl shadow-2xl">
            <div className="p-6 border-b flex justify-between items-center bg-gray-50">
                <h2 className="text-xl font-bold text-gray-800">{currentEmp.id ? 'Edit Employee' : 'New Employee'}</h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-200 rounded-full"><X /></button>
            </div>
            <div className="flex bg-white border-b px-6 overflow-x-auto">
              {TAB_CONFIG.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-6 py-4 text-sm font-bold border-b-2 transition-colors ${activeTab === tab.id ? "border-teal-600 text-teal-600 bg-teal-50" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto p-8">
              {renderTabContent()}
            </div>
            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-6 py-2 border rounded-xl font-bold">Cancel</button>
              <button onClick={handleSave} disabled={isSaving} className="px-8 py-2 bg-teal-600 text-white rounded-xl font-bold flex items-center gap-2 hover:bg-teal-700 disabled:opacity-50">
                {isSaving ? <Loader2 className="animate-spin" size={18}/> : <Save size={18}/>}
                Save Record
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
